import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: LinearZeroTask
   Cel: Oblicz miejsce zerowe funkcji liniowej f(x)=ax+b.
============================================================= */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function simplifyFrac(n, d) {
  const g = gcd(n, d);
  n /= g;
  d /= g;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  return [n, d];
}

// Zwraca latex:
// - liczba całkowita, jeśli da się uprościć do całości
// - ułamek właściwy/niewłaściwy jako mieszany, jeśli |n| > |d| i nie dzieli się
// - minus zawsze przed całością/ulamek, a nie w liczniku
function formatFracOrMixedLatex(n, d) {
  let [nn, dd] = simplifyFrac(n, d);

  if (dd === 1) return `${nn}`;

  const sign = nn < 0 ? "-" : "";
  const absN = Math.abs(nn);
  const absD = Math.abs(dd);

  // ułamek właściwy
  if (absN < absD) {
    return `${sign}\\dfrac{${absN}}{${absD}}`;
  }

  // ułamek niewłaściwy -> sprawdź czy całkowity
  const whole = Math.floor(absN / absD);
  const rest = absN % absD;

  if (rest === 0) {
    return `${sign}${whole}`;
  }

  // mieszany: np. 2 1/3 ; z minusem: -2 1/3
  return `${sign}${whole}\\dfrac{${rest}}{${absD}}`;
}

function formatLinearLatex(a, b) {
  const aStr = a === 1 ? "" : a === -1 ? "-" : String(a);
  const bStr = b === 0 ? "" : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
  return `${aStr}x${bStr}`;
}

export function generateTask(options = {}) {
  const a = randInt(-6, 6, new Set([0]));
  const b = randInt(-12, 12, new Set([0])); // niech nie będzie zawsze 0

  const num = -b;
  const den = a;
  const [n, d] = simplifyFrac(num, den);

  return {
    type: "LinearZeroTask",
    data: {
      a,
      b,
      answerNum: n,
      answerDen: d,
      answerVal: n / d,
      mentionLinearFunction: options.mentionLinearFunction !== false,
    },
  };
}

export function renderTask({ data }) {
  const { a, b } = data;
  return (
    <div style={{ marginTop: 6 }}>
      <div>
        Dana jest {data.mentionLinearFunction !== false ? "funkcja liniowa" : "funkcja"}:
        <BlockMath math={`f(x) = ${formatLinearLatex(a, b)}`} />
      </div>
      <div>Oblicz miejsce zerowe tej funkcji.</div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: 10 }}>
      <InlineMath math={`x_0 =`} />
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="x_0"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || isNaN(parsed)) return false;
  return Math.abs(parsed - data.answerVal) < 0.001;
}

export function renderAnswer({ data }) {
  const latex = formatFracOrMixedLatex(data.answerNum, data.answerDen);
  return <InlineMath math={`x_0 = ${latex}`} />;
}

export function renderExplanation({ data }) {
  const { a, b, answerNum, answerDen } = data;
  const latex = formatFracOrMixedLatex(answerNum, answerDen);

  const rhs = -b; // z ax + b = 0 -> ax = -b

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Rozwiązujemy równanie:
      </div>
      <BlockMath math={`${formatLinearLatex(a, b)} = 0`} />

      <div>
        <strong>Krok 2.</strong> Przenosimy wyraz wolny:
      </div>
      <BlockMath math={`${a}x ${b >= 0 ? "+ " + b : "- " + Math.abs(b)} = 0`} />
      <BlockMath math={`${a}x = ${rhs}`} />

      <div>
        <strong>Krok 3.</strong> Dzielimy przez <InlineMath math={`${a}`} />:
      </div>
      <BlockMath math={`x = \\dfrac{${rhs}}{${a}} = ${latex}`} />

      <div>
        Zatem <InlineMath math={`x_0 = ${latex}`} />.
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`x_0 = ${userAnswer.raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
